import { CircularProgress, Stack, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Card } from "~/components/Card";
import { Center } from "~/components/Center";
import {
  DetailsLayout,
  LockCard,
  RelatedResource,
} from "~/components/DetailsCards";
import {
  Form,
  FormSkeleton,
  getChangedFields,
  ObjectField,
  TextField,
  useStudioForm,
} from "~/components/Form";
import {
  OBJECT_KEY_DELIMITER,
  ObjectSearchRequestProvider,
} from "~/components/ObjectExplorer";
import { QueryRenderer } from "~/components/QueryRenderer";
import { ErrorMessage } from "~/components/error-message";
import { pick } from "~/lib/std";
import type { Ingestion } from "~/lqs";
import {
  LqsHistoryCard,
  LqsManageCard,
  LqsResourceFields,
  useDeleteIngestion,
  useIngestion,
  useUpdateIngestion,
} from "~/lqs";
import {
  calculateProcessRefetchInterval,
  Process,
} from "~/lqs/components/process";
import {
  makeEditIngestionLocation,
  makeIngestionPartsLocation,
  makeIngestionsLocation,
  makeLogObjectsLocation,
  useIngestionParams,
} from "~/paths";
import { selectData } from "~/utils";
import { LogObjectTable } from "../../logs/components/LogObjectTable";
import { editIngestionSchema } from "../schemas";
import {
  IngestionPartSearchRequestProvider,
  IngestionPartTable,
} from "./IngestionPartTable";

export function IngestionDetails() {
  const { ingestionId } = useIngestionParams();

  const query = useIngestion(ingestionId, {
    select: selectData,
    refetchInterval: calculateProcessRefetchInterval,
  });

  const generalSection = <GeneralSection query={query} />;
  const processSection = (
    <Process query={query} mutation={useUpdateIngestion(ingestionId)} />
  );
  const relatedResourcesSection = (
    <Stack spacing={2}>
      <Typography variant="h4" component="h2">
        Related Resources
      </Typography>
      <RelatedObjects query={query} />
      <RelatedResource
        text="Parts"
        to={makeIngestionPartsLocation({ ingestionId })}
        table={
          <IngestionPartSearchRequestProvider embedded>
            <IngestionPartTable />
          </IngestionPartSearchRequestProvider>
        }
      />
    </Stack>
  );
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="ingestion"
      query={query}
      updateMutation={useUpdateIngestion(ingestionId)}
    />
  );
  const historySection = <LqsHistoryCard query={query} />;
  const manageSection = (
    <LqsManageCard
      resourceName="ingestion"
      query={query}
      editLocation={makeEditIngestionLocation({ ingestionId })}
      deleteMutation={useDeleteIngestion(ingestionId)}
      getReadableName={(ingestion) => ingestion.name ?? ingestion.id}
      listLocation={makeIngestionsLocation()}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {processSection}
          {relatedResourcesSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {processSection}
          {lockSection}
          {historySection}
          {manageSection}
          {relatedResourcesSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Ingestion> }) {
  return (
    <Card title="General">
      <LqsResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "foreign-key", resourceType: "log", accessor: "logId" },
          { dataType: "text", accessor: "objectKey" },
          {
            dataType: "foreign-key",
            resourceType: "object-store",
            accessor: "objectStoreId",
          },
        ]}
      />
    </Card>
  );
}

function RelatedObjects({ query }: { query: UseQueryResult<Ingestion> }) {
  return (
    <QueryRenderer
      query={query}
      loading={
        <Card>
          <Center sx={{ height: 350 }}>
            <CircularProgress />
          </Center>
        </Card>
      }
      error={
        <Card>
          <ErrorMessage>Couldn't load objects</ErrorMessage>
        </Card>
      }
      success={(ingestion) => {
        const ingestionPrefix = ["ingestions", ingestion.id, ""].join(
          OBJECT_KEY_DELIMITER,
        );

        return (
          <RelatedResource
            text="Objects"
            to={makeLogObjectsLocation(ingestion.logId, {
              directory: ingestionPrefix,
            })}
            table={
              <ObjectSearchRequestProvider embedded>
                <LogObjectTable
                  logId={ingestion.logId}
                  homeName={ingestion.id}
                  subResourcePrefix={ingestionPrefix}
                />
              </ObjectSearchRequestProvider>
            }
          />
        );
      }}
    />
  );
}

function InfoSection({ query }: { query: UseQueryResult<Ingestion> }) {
  return (
    <Card>
      <QueryRenderer
        query={query}
        loading={<FormSkeleton shapes={["text", "multiline", "multiline"]} />}
        success={(ingestion) => <InfoSectionImpl ingestion={ingestion} />}
      />
    </Card>
  );
}

function InfoSectionImpl({ ingestion }: { ingestion: Ingestion }) {
  const updateIngestion = useUpdateIngestion(ingestion.id);

  const { enqueueSnackbar } = useSnackbar();

  const schema = editIngestionSchema.pick({
    name: true,
    note: true,
    context: true,
  });
  const FIELDS = schema.keyof().options;

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useStudioForm({
    schema,
    defaultValues: pick(ingestion, FIELDS),
    onSubmit(values) {
      const changedFields = getChangedFields(values, dirtyFields);

      updateIngestion.mutate(changedFields, {
        onSuccess(response) {
          enqueueSnackbar("Ingestion updated", { variant: "success" });

          reset(pick(response.data, FIELDS));
        },
        onError() {
          enqueueSnackbar("Unable to update ingestion", { variant: "error" });
        },
      });
    },
  });

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        loading={updateIngestion.isLoading}
        submitText="Save Changes"
        disabled={ingestion.locked}
      >
        <TextField control={control} name="name" />
        <TextField control={control} name="note" multiline />
        <ObjectField control={control} name="context" />
      </Form>
      {ingestion.locked && (
        <Typography variant="body2">
          Unlock this ingestion to make updates.
        </Typography>
      )}
    </>
  );
}
